// src/pages/ChatbotSettings/Prompt.js
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useParams} from 'react-router-dom';

import axios from "axios";
import backendURL from "../../config";
import CreatePrompts from "../Professions/CreatePrompts";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import { Typography, CircularProgress } from '@mui/material'; // For setup mode elements

const DEFAULT_PRIMARY_COLOR = "#353535";
const DEFAULT_SECONDARY_COLOR = "#ffffff";

// --- Color Picker Styles (can be moved to a separate file or kept here) ---
const colorPickerWrapperStyle = { /* ... your existing style ... */ };
const colorPreviewStyle = (color) => ({ /* ... your existing style ... */ });
const colorInputHiddenStyle = { /* ... your existing style ... */ };
const colorHexStyle = { /* ... your existing style ... */ };
// --- End Color Picker Styles ---

const Prompt = forwardRef(({
  chatbotId,
  isSetupMode = false,
  initialChatbotNameFromSetup = "", // Used in setup mode
  onComplete,
  onError
}, ref) => {
  const [message, setMessage] = useState("");
  const [chatbotName, setChatbotName] = useState(initialChatbotNameFromSetup || ""); // Initialize with prop if in setup
  const [chatbotNameError, setChatbotNameError] = useState("");
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY_COLOR);
  const [prompt, setPrompt] = useState("");
  const [greeting, setGreeting] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false); // For internal button feedback

  const primaryColorInputRef = useRef(null);
  const secondaryColorInputRef = useRef(null);
  const createPromptsRef = useRef(null);

  const token = localStorage.getItem("access_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const { chatbotId: paramsChatbotId } = useParams();
  let chatBotId = chatbotId ? chatbotId : paramsChatbotId

  const fetchLLMSettings = async (isInitialFetch = false, currentChatbotId) => {
    if (!token && !isSetupMode) { // In setup mode, we might not fetch if only setting prompt/greeting
        setMessage("Authentication token missing. Cannot load settings.");
        return;
    }
    // In setup mode, if initialChatbotName is provided, we primarily care about prompt/greeting.
    // If not in setup mode, or if it's an initial fetch for setup (to get existing prompt/greeting), then fetch.
    if (!isSetupMode || isInitialFetch) {
        try {
          const params = currentChatbotId ? { chatbot_id: currentChatbotId } : {};
          const response = await axios.get(`${backendURL}/get-llm-settings`, { headers,params });
          if (response.data && response.data.success && typeof response.data.data !== 'undefined') {
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

            // In setup mode, chatbotName is controlled by initialChatbotNameFromSetup
            if (!isSetupMode) {
              setChatbotName(responseData.chatbot_name || "");
            }
            setPrimaryColor(responseData.primary_color || DEFAULT_PRIMARY_COLOR);
            setSecondaryColor(responseData.secondary_color || DEFAULT_SECONDARY_COLOR);
            setPrompt(responseData.prompt_text || "");
            setGreeting(responseData.greeting_text || "");
          } else {
            // Handle non-success or bad data for non-setup mode
            if (!isSetupMode) {
              const errorMsg = (response.data && !response.data.success)
                ? response.data.message
                : "Could not load settings due to unexpected data format.";
              setMessage(errorMsg || "Could not load settings.");
            }
            // For setup mode, if fetch fails for prompt/greeting, it's okay, user will input new ones.
            setPrompt(""); // Default to empty if fetch fails for setup
            setGreeting("");
            setPrimaryColor(DEFAULT_PRIMARY_COLOR);
            setSecondaryColor(DEFAULT_SECONDARY_COLOR);
            console.warn("Chatbot settings data issue:", response.data);
          }
        } catch (error) {
          if (!isSetupMode) {
            setMessage("Error fetching Chatbot settings.");
          }
        
          setPrimaryColor(DEFAULT_PRIMARY_COLOR);
          setSecondaryColor(DEFAULT_SECONDARY_COLOR);
          setPrompt(""); // Default to empty on error
          setGreeting("");
          console.error("Error fetching Chatbot settings:", error);
        }
    } else if (isSetupMode && initialChatbotNameFromSetup) {
        setChatbotName(initialChatbotNameFromSetup); // Ensure name is set from prop
    }
  };

  useEffect(() => {
    // Fetch existing prompt/greeting even in setup mode for pre-fill,
    // but name and colors are handled differently.
    fetchLLMSettings(true, chatBotId); // Pass true to indicate it's an initial fetch context

    if (isSetupMode && initialChatbotNameFromSetup) {
        setChatbotName(initialChatbotNameFromSetup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatBotId, isSetupMode, initialChatbotNameFromSetup, token]); // Add token to dependencies


  const refreshLLMSettingsData = async (successMessage = "New prompt applied successfully!") => {
    // In setup mode, refresh only updates prompt/greeting from fetched data if any.
    // Name and colors are not re-fetched/set here.
    await fetchLLMSettings(false, chatBotId); // This will re-fetch prompt/greeting
    if (isSetupMode && initialChatbotNameFromSetup) {
        setChatbotName(initialChatbotNameFromSetup); // Re-assert name from prop
    }
    setMessage(successMessage);
    setIsDrawerOpen(false);
    setTimeout(() => setMessage(""), 3000);
  };

  const triggerSave = async () => { // Renamed from handleSave for ref
    setIsSaving(true);
    setIsSaved(false);
    setMessage("");
    setChatbotNameError("");

    // Use initialChatbotNameFromSetup if in setup mode and it's provided
    const currentName = (isSetupMode && initialChatbotNameFromSetup) ? initialChatbotNameFromSetup : chatbotName;

    if (!isSetupMode) { // Only validate name input if not in setup mode (or if name wasn't pre-filled in setup)
        if (currentName.trim().length > 30) {
            setChatbotNameError("Chatbot name must not be more than 30 characters long.");
            setIsSaving(false);
            if (isSetupMode && onError) onError("Chatbot name validation issue."); // Generic for setup
            return false;
        }
        if (!currentName.trim()) {
            setChatbotNameError("Chatbot name is required.");
            setIsSaving(false);
             if (isSetupMode && onError) onError("Chatbot name is required.");
            return false;
        }
    }


    if (!prompt.trim()) {
      const promptErrorMsg = "Prompt text cannot be empty.";
      setMessage(promptErrorMsg); // Show message locally
      setIsSaving(false);
      if (isSetupMode && onError) onError(promptErrorMsg);
      return false;
    }
    if (!greeting.trim()) {
      const greetingErrorMsg = "Greeting text cannot be empty.";
      setMessage(greetingErrorMsg); // Show message locally
      setIsSaving(false);
      if (isSetupMode && onError) onError(greetingErrorMsg);
      return false;
    }
     if (!token) {
        const noTokenMsg = "Authentication token missing. Cannot save settings.";
        if (isSetupMode && onError) onError(noTokenMsg); else setMessage(noTokenMsg);
        setIsSaving(false);
        return false;
    }

    const payload = {
      chatbot_name: currentName.trim(),
      prompt_text: prompt.trim(),
      greeting_text: greeting.trim(),
      primary_color: primaryColor,
      secondary_color: secondaryColor,
      is_chatbot_setting : isSetupMode,
      chatbot_id:chatBotId
    };

    try {
      const response = await axios.post(`${backendURL}/add-llm-settings`, payload, { headers });

      if (response.data && response.data.success) {
        // Update localStorage
        try {
            let userData = {};
            const userStrLc = localStorage.getItem('user');
            if (userStrLc) userData = JSON.parse(userStrLc);
            if (!userData.llm_settings) userData.llm_settings = {};
            userData.llm_settings.chatbot_name = payload.chatbot_name; // Should match initialChatbotNameFromSetup
            userData.llm_settings.prompt_text = payload.prompt_text;
            userData.llm_settings.greeting_text = payload.greeting_text;
            if (!isSetupMode) { // Only store colors if not in setup mode
                userData.llm_settings.primary_color = payload.primary_color;
                userData.llm_settings.secondary_color = payload.secondary_color;
            }
            if (!userData.chatbot_setup_configuration) userData.chatbot_setup_configuration = {};
            userData.chatbot_setup_configuration.is_prompt_set = true
            localStorage.setItem('user', JSON.stringify(userData));
            console.log("LLM settings (prompt/greeting) saved to localStorage for stepper.");
        } catch (e) {
            console.error("Prompt.js: Failed to update localStorage with LLM settings:", e);
            if (isSetupMode && onError) {
                onError("Settings saved, but failed to update local session data.");
                // return false; // Decide if this should halt the process
            }
        }

        if (isSetupMode && onComplete) {
          onComplete({ nextSlug: 'chatbot-training-info' });
        } else {
          setIsSaved(true);
          setMessage(response.data.message || "Chatbot settings have been saved successfully!");
          // No timeout for isSaved in setupMode, parent controls UI
        }
        return true; // Save successful
      } else {
        const errorMsg = response.data?.message || "Failed to save Chatbot settings.";
        if (isSetupMode && onError) onError(errorMsg);
        else setMessage(errorMsg);
        setIsSaved(false);
        return false; // Save failed
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || "An error occurred while saving Chatbot settings.";
      if (isSetupMode && onError) onError(errorMsg);
      else setMessage(errorMsg);
      setIsSaved(false);
      return false; // Save failed
    } finally {
      setIsSaving(false);
    }
  };

  // Expose triggerSave for the parent stepper
  useImperativeHandle(ref, () => ({
    triggerSave: triggerSave
  }));

  const toggleDrawer = (open) => () => {
    setIsDrawerOpen(open);
  };

  const createPromptSubmit = async () => {
    // ... (keep existing createPromptSubmit logic)
    // Ensure it updates the 'prompt' state, and then triggerSave can be called.
    if (createPromptsRef.current && createPromptsRef.current.handleGeneratePrompt) {
        // handleGeneratePrompt in CreatePrompts should return the generated text or true on success
        const generatedPrompt = await createPromptsRef.current.handleGeneratePrompt();
        if (typeof generatedPrompt === 'string' && generatedPrompt) {
          setPrompt(generatedPrompt); // Update local prompt state
          setMessage("New prompt generated. Review and save settings.");
          setIsDrawerOpen(false);
          // Don't auto-save here; let the user click "Save & Continue" or "Save Settings"
        } else if (generatedPrompt === true) { // If it only returns true
           // Need to re-fetch or get the prompt from CreatePrompts state if possible
           await refreshLLMSettingsData("New prompt generated and applied. Please save settings to confirm all changes.");
        }
      }
  };

  // --- Conditional rendering for setup mode ---
  const PageWrapper = isSetupMode ? Box : 'main'; // Use Box for setup, main for standalone
  const pageWrapperProps = isSetupMode ? { sx: { p: 0 } } : { className: "content page-website-configurations" };
  const ContainerWrapper = isSetupMode ? Box : 'div';
  const containerWrapperProps = isSetupMode ? { sx: { p: 0 } } : { className: "container-fluid p-0" };
  const CardWrapper = isSetupMode ? Box : 'div'; // No card in setup mode, stepper provides it
  const cardWrapperProps = isSetupMode ? {} : { className: "card" };
  const CardBodyWrapper = isSetupMode ? Box : 'div';
  const cardBodyWrapperProps = isSetupMode ? {sx: {p:1}} : { className: "card-body" };


  return (
    <PageWrapper {...pageWrapperProps}>
      <ContainerWrapper {...containerWrapperProps}>
        {isSetupMode && (
            <Box sx={{textAlign: 'center', mb:2}}>
                <Typography variant="h6">Configure Chatbot Behavior</Typography>
                <Typography variant="body2" color="textSecondary">
                  Configure the chatbot with a primary and secondary color and define the core prompt and initial greeting message for your chatbot.
                </Typography>
                 {initialChatbotNameFromSetup && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        Configuring for: <strong>{initialChatbotNameFromSetup}</strong>
                    </Typography>
                )}
            </Box>
        )}
        {/* Row and Col structure might be removed if stepper provides its own layout */}
        <div className={!isSetupMode ? "row" : ""}>
          <div className={!isSetupMode ? "col-12 col-lg-12" : ""}>
            <CardWrapper {...cardWrapperProps}>
              <CardBodyWrapper {...cardBodyWrapperProps}>
                <div className="row justify-content-center"> {/* Center content for setup mode */}
                  <div className={isSetupMode ? "col-12" : "col-8 col-lg-8"}>
                    {/* Chatbot Name Field - Conditionally Rendered/Disabled */}
                    {!isSetupMode && ( // Only show if not in setup mode
                      <div className="mb-4">
                        <h5 className="card-title mb-1">Chatbot Name: <span className="text-danger">*</span></h5>
                        <input
                          type="text"
                          id="chatbotNameInput"
                          name="chatbotName"
                          className={`form-control ${chatbotNameError ? 'is-invalid' : ''}`}
                          value={chatbotName} // Controlled by state
                          onChange={(e) => { /* ... existing onChange ... */ setChatbotName(e.target.value); /* basic for brevity */ }}
                          placeholder="Enter chatbot name (max. 30 characters)"
                          disabled={isSetupMode && !!initialChatbotNameFromSetup} // Disable if name is pre-filled in setup
                        />
                        {chatbotNameError && <div className="invalid-feedback d-block">{chatbotNameError}</div>}
                        {/* ... other name validation messages ... */}
                      </div>
                    )}

                    {/* Color Palettes - Conditionally Rendered */}
                      <div className="row mb-3">
                        <div className={!isSetupMode ? "col-md-6" : "col-md-3"}> {/* Use md for better responsiveness */}
                          <div className="mb-3">
                            <h5 className="card-title mb-1">Primary Color:</h5>
                            <div style={colorPickerWrapperStyle}>
                              <div style={colorPreviewStyle(primaryColor)} onClick={() => primaryColorInputRef.current?.click()} />
                              <input ref={primaryColorInputRef} type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} style={colorInputHiddenStyle} />
                              <div style={colorHexStyle}>{primaryColor.toUpperCase()}</div>
                            </div>
                          </div>
                        </div>
                        <div className={!isSetupMode ? "col-md-6" : "col-md-3"}>
                          <div className="mb-3">
                            <h5 className="card-title mb-1">Secondary Color:</h5>
                            <div style={colorPickerWrapperStyle}>
                              <div style={colorPreviewStyle(secondaryColor)} onClick={() => secondaryColorInputRef.current?.click()} />
                              <input ref={secondaryColorInputRef} type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} style={colorInputHiddenStyle} />
                              <div style={colorHexStyle}>{secondaryColor.toUpperCase()}</div>
                            </div>
                          </div>
                        </div>
                        { isSetupMode && (<><div className="col-md-3"></div><div className="col-md-3"></div></>)}
                      </div>

                    {/* Prompt Configuration */}
                    <div className="row mt-3">
                      <div className="col-6">
                        <h5 className="card-title mb-2">System Prompt:</h5>
                      </div>
                      <div className="col-6" style={{ textAlign: "end" }}>
                        <a id="GeneratePromptLink" onClick={toggleDrawer(true)} style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}>
                          Generate Prompt
                        </a>
                        <Tooltip title="The system prompt guides the AI's personality, role, and response style.">
                          <i className="fa fa-info-circle ms-1"></i>
                        </Tooltip>
                      </div>
                    </div>
                    <div className="mb-3">
                      <textarea
                        id="prompting" name="prompting" className="form-control"
                        value={prompt} onChange={(e) => setPrompt(e.target.value)}
                        style={{ height: isSetupMode? "150px" : "200px", width: "100%" }} // Slightly smaller for setup
                        placeholder="e.g., You are a helpful assistant for KlonIT. Answer questions about our services and provide support."
                      />
                    </div>

                    {/* Greeting Text Section */}
                    <div className="mt-3"> {/* Simplified structure for greeting */}
                      <h5 className="card-title mb-1">Greeting Message: <span className="text-danger">*</span></h5>
                      <div className="mb-3">
                        <textarea
                          id="greeting" name="greeting" className="form-control"
                          value={greeting} onChange={(e) => setGreeting(e.target.value)}
                          style={{ height: isSetupMode? "100px" : "200px", width: "100%" }} // Slightly smaller for setup
                          placeholder="e.g., Hello! I'm the KlonIT assistant. How can I help you today?"
                        />
                      </div>
                    </div>

                    {/* Save Button - Conditionally Rendered */}
                    {!isSetupMode && (
                      <div className="row mt-3">
                        <div className="col-12">
                          <button className="btn btn-primary" onClick={triggerSave} disabled={isSaving}>
                            {isSaving ? <><i className="fa fa-spinner fa-spin me-1"></i> Saving...</>
                              : isSaved ? <><i className="fa fa-check me-1"></i> Saved!</>
                                : <><i className="fa fa-save me-1"></i> Save Settings</>}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Message Display - Show internal messages or errors from stepper */}
                    {!isSetupMode && ( message && (
                    <div className="row mt-3">
                      <div className="col-12">
                        <div
                          id="alertMessage"
                          className={`alert ${isSaved && message.toLowerCase().includes("success") ? 'alert-success' :
                              (message.toLowerCase().includes("error") || message.toLowerCase().includes("failed") || message.toLowerCase().includes("cannot be empty") || message.toLowerCase().includes("required") ? 'alert-danger' : 'alert-info')
                            } ${message ? "" : "d-none"}`}
                          role="alert"
                        >
                          {message}
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
              </CardBodyWrapper>
            </CardWrapper>
          </div>
        </div>
      </ContainerWrapper>

      <Drawer anchor="right" open={isDrawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: { xs: '100%', sm: 500 }, p: 2, mt: 3 }} role="presentation">
            {/* ... Drawer content ... */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <h5 style={{ margin: 0 }}>Generate New Prompt</h5>
                <IconButton onClick={toggleDrawer(false)}><CloseIcon /></IconButton>
            </Box>
            <CreatePrompts
                ref={createPromptsRef}
                chatbotId={chatbotId}
                onFormValidityChange = {()=>{}}
                onComplete={(data) => { // CreatePrompts should call this with the text
                    setPrompt(data.prompt_text);
                    setGreeting(data.greeting_text);
                    setIsDrawerOpen(false);
                }}
                onError={(errorMessage) => {
                    setMessage(errorMessage || "Error during prompt generation.");
                    setIsDrawerOpen(false);
                }}
            />
            {/* The "Generate and Use Prompt" button in the drawer is now slightly confusing.
                It should probably just generate and set the prompt text in this component.
                The actual saving of all LLM settings (including the new prompt)
                will happen via the main "Save Settings" or stepper's "Save & Continue".
            */}
            <Box sx={{ mt: 2, textAlign: 'right' }}>
                <button className="btn btn-primary" onClick={createPromptSubmit}>
                    Generate & Apply to Form
                </button>
            </Box>
        </Box>
      </Drawer>
    </PageWrapper>
  );
});

export default Prompt;